import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Card,
  CardContent,
  Stack,
  Chip,
  Box,
  IconButton,
  Badge,
} from "@mui/material";
import { FilterPopover } from "../../../components/customFilterPopover";
import { usePopover } from "../../../components/customPopover";
import { useFormattedOptions } from "../../../hooks/use-data-formated";
import FilterListIcon from "@mui/icons-material/FilterList";
import DownloadIcon from "@mui/icons-material/Download";
import { getBatteryIds } from "../../../hooks/use-battery";
import { useQuery } from "@tanstack/react-query";
import { batteryMovementStatusOptions } from "../constant";
import { getStateNames } from "../../../hooks/use-state";
import RefreshIcon from "@mui/icons-material/Refresh";

const FilterBar = ({
  filters,
  onFilters,
  onResetFilters,
  hasActiveFilter,
  defaultFilters,
  onDownload,
  onRefresh,
}) => {
  const statusOptions = [
    {
      value: "",
      label: `All`,
      // label: `All (${totalCount})`,
      color: "default",
    },
    {
      value: "in-transit",
      label: `In Transit`,
      // label: `In Transit (${inTransitCount})`,
      color: "warning",
    },
    {
      value: "received",
      label: `Received`,
      color: "success",
    },
    {
      value: "cancelled",
      label: `Cancelled`,
      color: "error",
    },
    {
      value: "rejected",
      label: `Rejected`,
      color: "warning",
    },
  ];

  const [selctedFilters, setSelectedFilters] = useState(filters);
  const [stateNameList, setStateNameList] = useState([]);

  const popover = usePopover();

  const { data: batteryIdList, isLoading: isBatteryLoading } = useQuery({
    queryKey: ["batteryIds"],
    queryFn: () => getBatteryIds(""),
  });

  const { data: stateNameData, isLoading: isStateLoading } = useQuery({
    queryKey: ["stateNames"],
    queryFn: getStateNames,
  });

  useEffect(() => {
    if (stateNameData) {
      setStateNameList(stateNameData.map((item) => item.stateName));
    }
  }, [stateNameData]);

  const batteryIdOptions = useFormattedOptions(batteryIdList);
  const stateOptions = useFormattedOptions(stateNameList);

  const FILTER_FIELDS = useMemo(
    () => [
      {
        name: "batteryId",
        label: "Filter By Battery ID",
        type: "autocomplete",
        defaultExpanded: true,
        options: batteryIdOptions,
        isLoading: isBatteryLoading,
      },
      {
        name: "status",
        label: "Filter By Status",
        type: "select",
        defaultExpanded: false,
        options: batteryMovementStatusOptions,
      },
      {
        name: "fromState",
        label: "Filter By State From",
        type: "autocomplete",
        defaultExpanded: false,
        options: stateOptions,
        isLoading: isStateLoading,
      },
      {
        name: "toState",
        label: "Filter By State To",
        type: "autocomplete",
        defaultExpanded: false,
        options: stateOptions,
        isLoading: isStateLoading,
      },
      {
        name: "dateRange",
        label: "Date Range",
        type: "date-range",
      },
    ],
    [isBatteryLoading, batteryIdOptions, isStateLoading, stateOptions]
  );

  const handleFilters = useCallback((name, value) => {
    setSelectedFilters((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  }, []);

  useEffect(() => {
    setSelectedFilters({ ...filters });
  }, [filters]);

  const handleFiltersChange = useCallback(
    (newFilters) => {
      Object.entries(newFilters).forEach(([key, value]) => {
        handleFilters(key, value);
      });
    },
    [handleFilters]
  );

  const handleApplyFilters = useCallback(
    (appliedFilters) => {
      Object.entries(appliedFilters).forEach(([key, value]) => {
        onFilters(key, value);
      });
      popover.onClose();
    },
    [onFilters, popover]
  );

  return (
    <Card sx={{ my: 3 }}>
      <CardContent>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          gap={{ xs: 1, sm: 2 }}
          alignItems={{ xs: "flex-start", sm: "center" }}
        >
          {/* Quick Status Filter Chips */}
          <Box
            sx={{
              backgroundColor: "grey.100",
              p: 1,
              borderRadius: 2,
              display: "flex",
              gap: 1,
            }}
          >
            <Stack direction="row" flexWrap="wrap" gap={{ xs: 1, sm: 2 }}>
              {statusOptions.map((status) => {
                const isSelected = filters.status === status.value;

                return (
                  <Chip
                    key={status.value}
                    label={status.label}
                    color={isSelected ? status.color : "default"}
                    variant={isSelected ? "filled" : "outlined"}
                    onClick={() =>
                      handleApplyFilters({ ...filters, status: status.value })
                    }
                    size="small"
                    sx={{
                      fontWeight: 500,
                      borderRadius: 2,
                      borderColor: "grey.400",
                    }}
                  />
                );
              })}
            </Stack>
          </Box>
          <Box
            sx={{
              display: "flex",
              gap: 1,
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "flex-end",
              width: { xs: "100%", sm: "auto" },
            }}
          >
            <IconButton title="Refresh" onClick={onRefresh}>
              <RefreshIcon />
            </IconButton>

            <IconButton title="Download" onClick={onDownload}>
              <DownloadIcon />
            </IconButton>

            <IconButton
              title="Filter"
              color={popover.open ? "inherit" : "default"}
              onClick={popover.onOpen}
              sx={{ bgcolor: hasActiveFilter ? "#F6F6F6" : "transparent" }}
            >
              <Badge color="primary" variant="dot" invisible={!hasActiveFilter}>
                <FilterListIcon />
              </Badge>
            </IconButton>
          </Box>
        </Stack>
        <FilterPopover
          open={popover.open}
          onClose={popover.onClose}
          fields={FILTER_FIELDS}
          // selctedFilters={selctedFilters}
          onClear={onResetFilters}
          filters={selctedFilters}
          onFiltersChange={handleFiltersChange}
          onApply={handleApplyFilters}
          accordionMode={true}
          allowMultipleExpanded={true}
          showApplyButton={true}
          showClearButton={true}
          defaultFilters={defaultFilters}
        />
      </CardContent>
    </Card>
  );
};

export default FilterBar;
